<script lang="ts" setup>
import { onBeforeMount, ref, toRefs } from 'vue';
import Menu from 'primevue/menu';
import { useToast } from 'primevue/usetoast';
import { AxiosResponse } from 'axios';
import { useArchiveStore, useAuthStore } from '@/store';
import { PERMISSIONS } from '@/enum';
import { can } from '@/composables';

const emit = defineEmits(['onArchived']);
const authStore = useAuthStore();
const archiveStore = useArchiveStore();

const toast = useToast();

const props = defineProps({
  data: {
    type: Object,
    default: undefined,
  },
});

const { data } = toRefs(props);
const menu = ref();
const reasonId = ref();
const confirmArchiveOpen = ref(false);
const loading = ref(false);

let impersonateDebounce = false;

onBeforeMount(() => init());

const onImpersonate = async () => {
  if (impersonateDebounce) return;

  impersonateDebounce = true;

  await authStore.impersonate(data?.value?.id as number);

  if (authStore.error) {
    doFail(authStore.error);
    authStore.error = undefined;
    impersonateDebounce = false;
  }
};

const init = () => {
  loading.value = false;
  confirmArchiveOpen.value = false;
  reasonId.value = false;
};

const openConfirmArchive = () => {
  archiveStore.person = {
    id: data?.value?.id as number,
    display_name: data?.value?.full_name as string,
    archived_at: data?.value?.archived_at as string,
    people_type: {
      acronym: data?.value?.people_type.acronym as string,
    },
  };
  archiveStore.isShow = true;
};

const doToastUnarchive = (response: AxiosResponse) => {
  const success = response.status === 200;

  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? 'Unarchived' : 'Error',
    detail: success ? 'User Unarchived.' : response.statusText,
    life: 3000,
  });
};

const onUnarchivePerson = async () => {
  const response = await archiveStore.unarchive(data?.value?.id);

  if (response.status === 200) {
    emit('onArchived');
  }

  doToastUnarchive(response);
};

const menuItems = (() => {
  return [
    {
      label: 'View Profile',
      icon: {
        name: 'person',
        styles: 'inline-block text-lg text-blue-500',
      },
      styles: '',
      url: `/user/${data?.value?.id}`,
      visible: can([PERMISSIONS.PEOPLE_SHOW]),
    },
    {
      label: 'Impersonate',
      icon: {
        name: 'fingerprint',
        styles: 'inline-block text-lg text-purple-500',
      },
      command: onImpersonate,
      visible:
        !authStore.isImpersonating &&
        !!data?.value?.user &&
        !data?.value?.archived_at &&
        can([PERMISSIONS.IMPERSONATE]),
    },
    {
      label: 'Archive',
      icon: {
        name: 'vpn_key',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: openConfirmArchive,
      visible: data?.value?.archived_at === null && can([PERMISSIONS.PEOPLE_ARCHIVE]),
    },
    {
      label: 'Unarchive',
      icon: {
        name: 'unarchive',
        styles: 'inline-block text-lg text-orange-500',
      },
      command: onUnarchivePerson,
      visible: data?.value?.archived_at != null && can([PERMISSIONS.PEOPLE_UNARCHIVE]),
    },
  ];
})();

const toggle = (event: Event): void => menu.value.toggle(event);

const doFail = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: detail,
    life: 3000,
  });
};
</script>

<template>
  <div class="ml-3 cursor-pointer" @click="toggle">
    <Icon name="more_horiz" class="text-lg p-1" />
    <Menu id="overlay_menu" ref="menu" class="mt-2 ml-2 p-2" :model="menuItems" :popup="true">
      <template #item="{ item }">
        <div class="p-2">
          <a v-if="item.url" :href="item.url">
            <Icon :name="item.icon?.name" :class="item.icon.styles" />
            <span class="relative inline-block ml-2 text-sm text-800" v-text="item.label" />
          </a>
          <a v-if="item.command" @click="item.command()">
            <Icon :name="item.icon.name" :class="item.icon.styles" />
            <span class="relative inline-block ml-2 text-sm text-800" v-text="item.label" />
          </a>
        </div>
      </template>
    </Menu>
  </div>
</template>

<style lang="scss" scoped>
a {
  span {
    top: -4px;
  }
}
</style>
